import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { startRun } from "@/api/runs";
import { getQuestionSet } from "@/api/question-sets";
import {
  applyLanguagePlacement,
  getLanguagePlacementStatus,
} from "@/api/learner";
import { compose } from "@/practice/session-composer";
import { Player, type PlayItem } from "@/practice/player";
import { useRun } from "./use-run";
import { beginAndPostRun } from "./active-runs-store";
import { takePendingPlacement } from "./placement-handoff";
import { Spinner } from "@/components/ui";

/** The per-language placement flow, played as a STANDALONE diagnostic session
 *  (separate from the normal bulk training): check status → generate the
 *  `language-placement` set → play it → submit (`applyLanguagePlacement`) →
 *  start the real language-trainer run with the placed level. */
export function PlacementPlay() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [ctx] = useState(() => takePendingPlacement());
  const [runId, setRunId] = useState<string | null>(null);
  const startedRunRef = useRef(false);
  const proceededRef = useRef(false);

  // Fire the real language-trainer run (optionally with the placed level) and
  // return to Create. Guarded so it only ever happens once.
  const proceed = (level?: string | null) => {
    if (!ctx || proceededRef.current) return;
    proceededRef.current = true;
    const startInputs = level
      ? { ...ctx.startInputs, level, cefr_level: level }
      : ctx.startInputs;
    beginAndPostRun({
      frameworkSlug: ctx.frameworkSlug,
      frameworkTitle: ctx.frameworkTitle,
      startInputs,
    });
    navigate("/app/create");
  };

  // No pending placement (e.g. page reload) → bounce to Create.
  useEffect(() => {
    if (!ctx) navigate("/app/create", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1. Status check — skip placement entirely if not needed.
  const statusQ = useQuery({
    queryKey: ["placement-status", ctx?.language],
    queryFn: () => getLanguagePlacementStatus(ctx!.language),
    enabled: !!ctx,
  });
  useEffect(() => {
    if (!statusQ.data || !ctx) return;
    if (!statusQ.data.needs_placement) {
      proceed(statusQ.data.cefr_level);
      return;
    }
    if (!startedRunRef.current) {
      startedRunRef.current = true;
      startRun("language-placement", { target_language: ctx.language })
        .then((r) => setRunId(r.id))
        .catch(() => proceed()); // generation failed → start the run anyway
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusQ.data]);

  // 2. Poll the placement run until it produces a set.
  const { query: runQ } = useRun(runId ?? "");
  const producedSetId =
    runQ.data?.status === "complete" ? runQ.data.produced_question_set_id : null;
  useEffect(() => {
    if (runQ.data?.status === "failed") proceed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runQ.data?.status]);

  // 3. Fetch the produced set + compose a standalone session.
  const setQ = useQuery({
    queryKey: ["question-set", producedSetId],
    queryFn: () => getQuestionSet(producedSetId!),
    enabled: !!producedSetId,
  });
  const composed = useMemo(() => {
    if (!setQ.data) return null;
    return compose({
      sets: [setQ.data],
      mode: "normal",
      bulkCount: Math.max(1, setQ.data.bulks.length),
      wrongLimit: 0,
      focus: { kind: "wholeSet", setId: setQ.data.id },
      hideSeenInfo: false,
    });
  }, [setQ.data]);

  // Empty placement set (nothing valid) → just start the run.
  useEffect(() => {
    if (composed && composed.items.length === 0) proceed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composed]);

  // 4. Play it.
  if (ctx && composed && composed.items.length > 0) {
    const items: PlayItem[] = composed.items.map((ci) => ({
      item: ci.item,
      setId: ci.ctx.setId,
      topicSlug: ci.ctx.topicSlug,
      bulkId: ci.ctx.bulkId,
      bulkGradedSequence: ci.ctx.bulkGradedSequence,
      frameworkSlug: ci.ctx.frameworkSlug,
      sharedPassageText: ci.ctx.sharedPassageText,
    }));
    return (
      <Player
        items={items}
        anchorSetId={composed.anchorSetId ?? items[0]?.setId ?? ""}
        backTo="/app/create"
        backLabel={t("Skip")}
        onCompleted={(sessionId) => {
          applyLanguagePlacement(sessionId, ctx.language)
            .then((res) => proceed(res.cefr_level))
            .catch(() => proceed());
        }}
      />
    );
  }

  // Loading / generating state.
  return (
    <div className="mx-auto max-w-md animate-fade-up py-20 text-center">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
        <Spinner className="h-7 w-7" />
      </div>
      <h1 className="text-2xl font-bold tracking-tight">{t("Finding your level…")}</h1>
      <p className="mt-1 text-ink-faint">
        {t("Eddi is building a short placement quiz so it can start you in the right place.")}
      </p>
    </div>
  );
}
